import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/all";

import styles from "./moonshot.module.scss";

const Moonshot = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const smallDevice = windowWidth <= 990;
  const largeDevice = windowWidth > 990;

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    setWindowWidth(window.innerWidth);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const mainTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: "#moonshotWrapper",
        scrub: true,
        end: "+=750",
      },
    });

    const moonTimeline = gsap.timeline();
    moonTimeline.from("#moon", {
      y: 350,
      duration: 90,
      opacity: 0.8,
      delay: 4,
    });

    const textTimeline = gsap.timeline();
    textTimeline.from("#textContent", {
      y: 500,
      duration: 95,
      opacity: 0.5,
      delay: 5,
    });

    mainTimeline.add(moonTimeline, 12);
    mainTimeline.add(textTimeline, 24);
  }, []);

  return (
    <section className={styles.moonshotWrapper} id="moonshotWrapper">
      <div className={styles.sceneSection}>
        <div className={styles.moonWrapper}>
          <img
            className={styles.moon}
            id="moon"
            src={"/images/moonshot/moon.png"}
            alt=""
          />
          <div className={styles.textContent} id="textContent">
            <p className={styles.theMoonshot}>
              <span>The</span>
              <span>Moonshot!</span>
            </p>
            <p className={styles.staffFavorite}>Our Staff Favorite</p>
            <a href="https://campaigns-linkurious.cleverstory.io/banking-resources-linkurious" target="_blank">
              <img
                className={styles.moonshotLogo}
                src={"/images/moonshot/linkuriousLogo.png"}
              />
            </a>
          </div>
        </div>
        <img
          className={styles.terrainImg}
          src={
            largeDevice
              ? "/images/moonshot/terrain_desktop.png"
              : "/images/moonshot/terrain_mobile.png"
          }
        />
        <img
          className={styles.astronaut}
          src={"/images/moonshot/astronaut.png"}
          alt=""
        />
      </div>

      <div className={styles.contentSection}>
        {largeDevice &&
          <div className={styles.imageSection}>
            <a href="https://campaigns-linkurious.cleverstory.io/banking-resources-linkurious" target="_blank">
              <img
                className={styles.websiteImg} src="/images/moonshot/linkurious.png"
              />
            </a>
            <img
              className={styles.builtIn2days}
              src={"/images/moonshot/builtIn2days.png"}
              alt=""
            />
            <img
              className={styles.builtWithCleverstory}
              src={"/images/moonshot/builtWithCleverstory.png"}
              alt=""
            />
          </div>
        }
        <div className={styles.textSection}>
          <p className={styles.timeline}>MARCH, 2026</p>
          <h2 className={styles.heading}>
            When You Stop Trying to Speak to Everyone, You Finally Speak Clearly to Someone
          </h2>
          <div className={styles.QnA}>
            {smallDevice &&
              <div className={styles.imageSection}>
                <img
                  className={styles.websiteImg} src="/images/moonshot/linkurious.png"
                />

                <img
                  className={styles.builtIn2days}
                  src={"/images/moonshot/builtIn2days.png"}
                  alt=""
                />
                <img
                  className={styles.builtWithCleverstory}
                  src={"/images/moonshot/builtWithCleverstory.png"}
                  alt=""
                />
              </div>
            }
            <div className={styles.answer}>
              <span className={styles.paragraph}>
                Here's how most companies’ resource hubs work:
              </span>

              <span className={styles.paragraph}>
                "450+ Resources Available"
              </span>

              <span className={styles.paragraph}>
                Across Healthcare Compliance. Retail fraud. Government investigations. Banking AML. Insurance risk. Cryptocurrency monitoring. Fintech security. Supply chain analytics.
              </span>

              <span className={styles.paragraph}>
                Forty-seven case studies. Eighty blog posts. All mixed together.
              </span>

              <span className={styles.paragraph}>
                Somewhere in there are the 6 resources relevant to your work. Finding them? Your job.
              </span>

              <span className={styles.paragraph}>
                You click. "Retail Fraud Prevention" - wrong. "Healthcare Compliance" - nope. "Government Investigations" - not it.
              </span>

              <span className={styles.paragraph}>
                Ten minutes later, still searching. You leave.
              </span>

              <span className={styles.paragraph}>
                Now land on Linkurious's banking resource hub.
              </span>

              <span className={styles.paragraph}>
                Every single resource is about one thing: fighting financial crime in banking.
              </span>

              <span className={styles.paragraph}>
                "Mule Accounts & Money Laundering." "How Ria Money Transfer Cut Investigation Time by 80%." "Digital Banking Fraud Detection." "UBO Verification Best Practices."
              </span>

              <span className={styles.paragraph}>
                Everything speaks to your problem. No hunting.
              </span>

              <span className={styles.paragraph}>
                Here's what makes this work: Linkurious didn't just organize better. They built separate resource hubs for different audiences.
              </span>

              <span className={styles.paragraph}>
                Banking teams see the banking hub. Retail fraud teams see different content. Government compliance gets their own hub.
              </span>

              <span className={styles.paragraph}>
                Same company. Different experiences.
              </span>

              <span className={styles.paragraph}>
                Cleverstory made this scalable. Create multiple tailored resource hubs without rebuilding from scratch each time.
              </span>

              <span className={styles.paragraph}>
                As Kathryn Peake said: "tailored content around a theme for any number of given audiences."
              </span>

              <span className={styles.paragraph}>
                That phrase matters: "any number of given audiences." Not one hub for everyone. Multiple hubs, each for someone specific. Just what you need.
              </span>
              
              <span className={styles.paragraph}>
                Most companies build one resource hub and make visitors hunt. Linkurious built hubs that know who you are and show what matters.
              </span>
            </div>
            <div className={styles.liveExperienceLink}>
              <a href="https://campaigns-linkurious.cleverstory.io/banking-resources-linkurious" target="_blank">
                Live Experience
              </a>
            </div>
            {smallDevice &&
              <img
                className={styles.astronautImage}
                src={"/images/astronaut.png"}
                alt=""
              />
            }
          </div>
        </div>
      </div>
    </section>
  );
};

export default Moonshot;
